import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import join from 'url-join';
import { WsUrl } from '../config.json';
import { setUnreadCounts, setDeletableMsgMap } from '../store/ClinicalSimulation/clinicalSimulation';
import { ERROR } from '../util/const';
import '../util/string';
import useAuth from './useAuth';

const privateChatRoles = ['SADMIN', 'ADMIN', 'DISTR'];
const intlChatRoles = ['SADMIN', 'ADMIN'];

const useChatUnreadCounts = (caseId, { onDeleteMessageError } = {}) => {
    const auth = useAuth();
    const userRoleRef = useRef(auth?.user?.role ?? null);
    useEffect(() => {
        userRoleRef.current = auth?.user?.role ?? null;
    }, [auth?.user?.role]);

    const dispatch = useDispatch();
    const deletableMsgMap = useSelector((store) => store.clinicalSimulation.deletableMsgMap);

    // Keep latest callback/map in refs to avoid stale closures in socket handlers
    const onDeleteMessageErrorRef = useRef(onDeleteMessageError);
    useEffect(() => {
        onDeleteMessageErrorRef.current = onDeleteMessageError;
    }, [onDeleteMessageError]);

    const deletableMsgMapRef = useRef(deletableMsgMap);
    useEffect(() => {
        deletableMsgMapRef.current = deletableMsgMap;
    }, [deletableMsgMap]);

    // ── React state for Chat UI rendering ────────────────────────────────────
    const [chatRoomMessages, setChatRoomMessages] = useState([]);
    const [privateChatMessages, setPrivateChatMessages] = useState([]);
    const [intlChatMessages, setIntlChatMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(true);

    // ── Mutable ref for all data that must be current inside socket callbacks ─
    const state = useRef({
        currentUserId: parseInt(JSON.parse(localStorage.getItem('auth-token'))),
        currentDistrGroupId: parseInt(JSON.parse(localStorage.getItem('distr_group_id'))),
        currentAdminGroupId: parseInt(JSON.parse(localStorage.getItem('admin_group_id'))),

        chatRoomMessages: [],
        privateChatMessages: [],
        intlChatMessages: [],

        chatRoomCount: 0,
        privateChatCount: 0,
        intlChatCount: 0,

        lastSeen: undefined,
        privateLastSeen: undefined,
        intlLastSeen: undefined,
    });

    const socketRef = useRef(null);
    const isStopped = useRef(false);
    const retryCount = useRef(0);
    const retryTimer = useRef(null);
    const heartbeatTimer = useRef(null);

    const dispatchCounts = useCallback(() => {
        const { chatRoomCount, privateChatCount, intlChatCount } = state.current;
        dispatch(
            setUnreadCounts({
                chatRoom: chatRoomCount,
                privateChat: privateChatCount,
                intlChat: intlChatCount,
            })
        );
    }, [dispatch]);

    const processMessages = useCallback(
        (incomingMsgs) => {
            incomingMsgs = Array.ensureArray(incomingMsgs);

            const { msgs, deletedChatIds } = incomingMsgs.reduce(
                ({ msgs, deletedChatIds }, msg) => {
                    if (msg.type === 'DELETE') {
                        deletedChatIds.push(parseInt(msg.data));
                    } else {
                        msgs.push(msg);
                    }
                    return { msgs, deletedChatIds };
                },
                { msgs: [], deletedChatIds: [] }
            );

            const { currentUserId, currentDistrGroupId, currentAdminGroupId } = state.current;

            let {
                chatRoomMessages: crm,
                privateChatMessages: pcm,
                intlChatMessages: icm,
                chatRoomCount,
                privateChatCount,
                intlChatCount,
                lastSeen,
                privateLastSeen,
                intlLastSeen,
            } = state.current;

            if (deletedChatIds.length) {
                crm = crm.filter((msg) => !deletedChatIds.includes(parseInt(msg.chat_id)));
                pcm = pcm.filter((msg) => !deletedChatIds.includes(parseInt(msg.chat_id)));
                icm = icm.filter((msg) => !deletedChatIds.includes(parseInt(msg.chat_id)));
            }

            [0, 3, 4].forEach((room) => {
                if ((room === 3 && !privateChatRoles.includes(userRoleRef.current)) || (room === 4 && !intlChatRoles.includes(userRoleRef.current))) {
                    return;
                }
                const curMsgs = msgs.filter((msg) => parseInt(msg.room) === room && msg.type !== 'SEEN');

                const newLastSeen =
                    msgs.reduce((acc, msg) => {
                        if (parseInt(msg.room) === 3 || parseInt(msg.room) === 4) {
                            if (
                                msg.type === 'SEEN' &&
                                parseInt(msg.room) === room &&
                                parseInt(msg.user_id) === currentUserId &&
                                (!acc || (msg.last_seen_date && new Date(msg.last_seen_date) > acc))
                            ) {
                                return new Date(msg.last_seen_date);
                            }
                        } else if (parseInt(msg.room) === 0) {
                            if (
                                msg.type === 'SEEN' &&
                                parseInt(msg.room) === room &&
                                parseInt(msg.user_id) === currentUserId &&
                                (!acc || (msg.last_seen_date && new Date(msg.last_seen_date) > acc))
                            ) {
                                return new Date(msg.last_seen_date);
                            }
                        }
                        return acc;
                    }, undefined) ?? (room === 3 ? privateLastSeen : room === 4 ? intlLastSeen : lastSeen);

                switch (room) {
                    case 0: {
                        crm = crm.concat(curMsgs);
                        if (newLastSeen || deletedChatIds.length) {
                            chatRoomCount = crm.filter(
                                ({
                                    type,
                                    sender_id,
                                    sender_distr_group_id,
                                    sender_admin_group_id,
                                    sent_date,
                                    start_read_date,
                                }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    (!newLastSeen || new Date(sent_date) > newLastSeen) &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        } else {
                            chatRoomCount += curMsgs.filter(
                                ({
                                    type,
                                    sender_id,
                                    sender_distr_group_id,
                                    sender_admin_group_id,
                                    sent_date,
                                    start_read_date,
                                }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        }
                        lastSeen = newLastSeen;
                        break;
                    }
                    case 3: {
                        pcm = pcm.concat(curMsgs);
                        if (newLastSeen || deletedChatIds.length) {
                            privateChatCount = pcm.filter(
                                ({ type, sender_id, sent_date, start_read_date }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    (!newLastSeen || new Date(sent_date) > newLastSeen) &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        } else {
                            privateChatCount += curMsgs.filter(
                                ({ type, sender_id, sent_date, start_read_date }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        }
                        privateLastSeen = newLastSeen;
                        break;
                    }
                    case 4: {
                        icm = icm.concat(curMsgs);
                        if (newLastSeen || deletedChatIds.length) {
                            intlChatCount = icm.filter(
                                ({ type, sender_id, sent_date, start_read_date }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    (!newLastSeen || new Date(sent_date) > newLastSeen) &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        } else {
                            intlChatCount += curMsgs.filter(
                                ({ type, sender_id, sent_date, start_read_date }) =>
                                    type !== 'SYSTEM' &&
                                    parseInt(sender_id) !== currentUserId &&
                                    new Date(sent_date) > new Date(start_read_date)
                            ).length;
                        }
                        intlLastSeen = newLastSeen;
                        break;
                    }
                    default:
                        break;
                }
            });

            // Persist back to ref
            state.current = {
                ...state.current,
                chatRoomMessages: crm,
                privateChatMessages: pcm,
                intlChatMessages: icm,
                chatRoomCount,
                privateChatCount,
                intlChatCount,
                lastSeen,
                privateLastSeen,
                intlLastSeen,
            };

            // Update React state so Chat UI re-renders
            setChatRoomMessages([...crm]);
            setPrivateChatMessages([...pcm]);
            setIntlChatMessages([...icm]);

            dispatchCounts();
        },
        [dispatchCounts]
    );

    const setupSocket = useCallback(
        (socket) => {
            if (++retryCount.current > 50) return;

            const reconnect = () => {
                if (![socket.CONNECTING, socket.OPEN].includes(socket.readyState) && !retryTimer.current) {
                    retryTimer.current = setTimeout(() => {
                        try {
                            socket.onclose = socket.onerror = socket.onmessage = null;
                            socket.close();
                        } finally {
                        }
                        if (!isStopped.current) {
                            const newSocket = new WebSocket(join(WsUrl, caseId.toString()));
                            setupSocket(newSocket);
                            socketRef.current = newSocket;
                            retryTimer.current = null;
                        }
                    }, 5000);
                }
            };

            socket.onerror = () => {
                if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
                reconnect();
            };

            socket.onopen = () => {
                if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
                heartbeatTimer.current = setInterval(() => socket.send(JSON.stringify({ type: 'PING' })), 10000);
            };

            socket.onmessage = ({ data: dataStr }) => {
                setLoadingMessages(false);
                const data = JSON.parse(dataStr);
                if (data.success) {
                    processMessages(data.chat);
                } else if (data.message?.startsWith(ERROR.FAILED_TO_DELETE_CHAT_MESSAGE_CID)) {
                    const chatId = parseInt(data.message.split(':')[1]);
                    const msgKey = data.message.split(':')[0];
                    // Remove from deletable map in Redux
                    const currentMap = deletableMsgMapRef.current;
                    if (currentMap?.has(chatId)) {
                        const updated = new Map(currentMap);
                        updated.delete(chatId);
                        dispatch(setDeletableMsgMap(updated));
                    }
                    // Notify consumer to show error modal (needs translation access)
                    if (onDeleteMessageErrorRef.current) {
                        onDeleteMessageErrorRef.current({ chatId, msgKey });
                    }
                }
            };

            socket.onclose = () => {
                if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
                reconnect();
            };
        },
        [caseId, processMessages, dispatch]
    );

    useEffect(() => {
        if (!caseId) return;

        isStopped.current = false;
        retryCount.current = 0;
        const socket = new WebSocket(join(WsUrl, caseId.toString()));
        socketRef.current = socket;
        setupSocket(socket);

        return () => {
            isStopped.current = true;
            if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
            if (retryTimer.current) clearTimeout(retryTimer.current);
            try {
                socket.onclose = socket.onerror = socket.onmessage = null;
                socket.close();
            } catch (_) {}
        };
    }, [caseId, setupSocket]);

    return { socketRef, chatRoomMessages, privateChatMessages, intlChatMessages, loadingMessages };
};

export default useChatUnreadCounts;
